import qs from 'qs';
import auth from '~/auth';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import { sanitizeSchemaPatterns } from '~/utils/rjsfValidator';

import { customerPortalHost, json_headers, request } from './common';

const domains = {
  scoopon: window.configs.SCOOPON_API_DOMAIN,
  cudo: window.configs.CUDO_API_DOMAIN,
  treatme: window.configs.TREATME_API_DOMAIN,
  deals: window.configs.DEALS_API_DOMAIN,
  kogantravel: window.configs.KOGANTRAVEL_API_DOMAIN,
  lebusinesstraveller: window.configs.LEBUSINESSTRAVELLER_API_DOMAIN,
  leagenthub: window.configs.LEAGENTHUB_API_DOMAIN,
  leagenthub_cvfr: window.configs.LEAGENTHUB_CVFR_API_DOMAIN,
};

function alphabetiseRoleList(roleEnums, roleEnumNames) {
  const sortedPairs = roleEnums
    .map((role, index) => [role, roleEnumNames[index]])
    .sort(([a], [b]) => a.localeCompare(b));

  const sortedRoleEnums = sortedPairs.map(([role]) => role);
  const sortedRoleEnumNames = sortedPairs.map(([, name]) => name);

  return { sortedRoleEnums, sortedRoleEnumNames };
}

export type IdentityProvider = {
  id: string;
  name: string;
  identifier_claim_name: string;
};

export type AccountIdentity = {
  id: string;
  identity_provider_id: string;
  identifier_claim_value: string;
};

export type UserCustomClaim = {
  name: string;
  value: unknown;
};

export type UserCustomClaims = Array<UserCustomClaim>;

export type PatchUserPayload = {
  custom_claims?: UserCustomClaims;
};

export default class UsersService {
  static basePath() {
    return window.configs.AUTH_HOST + '/api/users';
  }

  static async getImpersonationToken(customerId: string, brand?: string) {
    const accessToken = await auth.getAccessToken({
      impersonation: {
        customerId,
        brand,
      },
    });

    if (!accessToken) {
      throw new Error('You are not logged in.');
    }

    return accessToken;
  }

  static async getUser(id, opts?: { brand?: string; signal?: AbortSignal; cache?: RequestCache }) {
    const { brand, signal, cache } = opts || {};
    const query = qs.stringify({
      ...(brand ? { brand } : {}),
    });
    const jsonBody = await request(`${this.basePath()}/${id}${query ? `?${query}` : ''}`, {
      method: 'GET',
      ...(signal ? { signal } : {}),
      ...(cache ? { cache } : {}),
    });
    return jsonBody.result;
  }

  static async getUserByUrl(url) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}${url}`, { method: 'GET' });
    return jsonBody.result;
  }

  static async getUserSummary(id, brand) {
    const params = { brand };

    return await request(`${this.basePath()}/summary/${id}?${qs.stringify(params)}`, {
      method: 'GET',
    });
  }

  static async getUsersSummaryByIds(ids, brand) {
    const BATCH_SIZE = 25;
    const batches = [];

    // Split ids into batches of 25 - the max number of ids that can be passed to the API
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      batches.push(ids.slice(i, i + BATCH_SIZE));
    }

    // Make parallel requests for each batch
    const batchPromises = batches.map((batch) => {
      const params = {
        brand,
        filter: batch,
      };

      return request(`${this.basePath()}/summary?${qs.stringify(params, { arrayFormat: 'repeat' })}`, {
        method: 'GET',
      });
    });

    const results = await Promise.all(batchPromises);
    const allUsers = results.flat();

    return new Map(allUsers.map((user) => [user.id_member, user]));
  }

  static async getUserTenancy(email) {
    let sanitizedUserEmail = email;

    // get rid of the # if its at the start of an email address
    // why? because this is being appended to a URL, and leaving it in will cause
    // it to be parsed as an anchor
    if (email.startsWith('#')) {
      sanitizedUserEmail = email.slice(1, email.length);
    }

    sanitizedUserEmail = encodeURIComponent(sanitizedUserEmail);

    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/get-user-tenants/${sanitizedUserEmail}`, {
      method: 'GET',
    });
    return jsonBody.result;
  }

  static async spoofUser(id, tenant, offerLink = '') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(tenant)}${offerLink}?utm_source=${
      tenant.brand
    }&utm_medium=admin&utm_campaign=spoof`;
  }

  static async spoofUserStore(id, tenant, offerLink = '', utmMedium = 'popup_store') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(
      tenant,
    )}${offerLink}?utm_medium=${utmMedium}&utm_source=${tenant.brand}&utm_campaign=spoof`;
  }

  static async spoofUserSearch(id, tenant) {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return {
      baseUrl: `${apiDomain}/redirect?token=${accessToken}`,
      queryParams: `utm_medium=popup_store&utm_source=${tenant.brand}&utm_campaign=spoof`,
    };
  }

  static async spoofUserQuote(id, tenant, cartId) {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(
      tenant,
    )}/checkout-restore-cart?cartId=${cartId}&utm_medium=admin&utm_source=${
      tenant.brand
    }&utm_campaign=agent_purchased_quote`;
  }

  static async spoofUserTrip(id, tenant, tripLink = '') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(
      tenant,
    )}${tripLink}?utm_medium=popup_store&utm_source=${tenant.brand}&utm_campaign=spoof`;
  }

  static async forceLogout(id, email, password) {
    await request(`${window.configs.AUTH_HOST}/api/users/${id}/invalidate-tokens`, {
      method: 'POST',
      headers: json_headers,
    });
    await request(`${window.configs.AUTH_HOST}/api/users/${id}`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ email, password }),
    });
  }

  static async spoofUserWithOfferLink(id, offer, tenant) {
    const accessToken = await this.getImpersonationToken(id);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;
    const offer_url = customerPortalHost(tenant) + `/offer/${offer.slug}/${offer.id_salesforce_external}`;

    return `${apiDomain}/redirect?token=${accessToken}&url=${offer_url}?`;
  }

  static async spoofUserTokenOnly(id) {
    return this.getImpersonationToken(id);
  }

  static async spoofUserWithOfferDeepLink(id, tenant, offerLink = '', utmMedium = 'admin') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${offerLink}&utm_source=${tenant.brand}&utm_medium=${utmMedium}&utm_campaign=spoof`;
  }

  static getUserLogins(id, limit = 20) {
    return request(this.basePath() + '/logins/' + id + `?limit=${limit}`, { method: 'GET' });
  }

  static getUsers({ filter, limit = 50, offset = 0, brand }, options = undefined) {
    const queryParams = {
      filter,
      limit,
      offset,
      brand,
    };

    return request(this.basePath() + `?${qs.stringify(queryParams)}`, options);
  }

  static getAdminUsers(filter, limit = 50, offset = 0) {
    const queryParams = {
      filter,
      limit,
      offset,
    };

    return request(`${window.configs.AUTH_HOST}/api/admin-users?${qs.stringify(queryParams)}`);
  }

  static getEmployeeUsers(filter?: string, limit = 20, offset = 0, roles?: Array<string>) {
    const queryParams: Record<string, string | number> = { limit, offset };
    if (filter != null && filter !== '') {
      queryParams.filter = filter;
    }
    if (roles?.length) {
      queryParams.roles = roles.join(',');
    }
    return request(`${window.configs.AUTH_HOST}/api/auth/employee-users?${qs.stringify(queryParams)}`);
  }

  static getVendorUsers(vendorId, limit = 50, offset = 0) {
    // TODO: Use qs.stringify
    return request(
      `${window.configs.AUTH_HOST}/api/vendor-users?vendor_id=${vendorId}&limit=${limit}&offset=${offset}`,
    );
  }

  static async patch(data) {
    const jsonBody = await request(`${this.basePath()}/${data.id_account}?${data.params}`, {
      method: 'PATCH',
    });
    return jsonBody.result;
  }

  static async patchUser(idMember: string, payload: PatchUserPayload) {
    const jsonBody = await request(`${this.basePath()}/${idMember}`, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
    return jsonBody.result as App.User;
  }

  static async getUpdateSchema(data, brand?: Brand) {
    const jsonBody = await request(`${this.basePath()}/${data.id_member}?brand=${brand}`, {
      method: 'OPTIONS',
    });

    if (jsonBody?.put?.properties?.roles?.items?.enum) {
      // svc-auth returns the roles in a random order, so we need to sort them for UX
      const { sortedRoleEnums, sortedRoleEnumNames } = alphabetiseRoleList(
        jsonBody.put.properties.roles.items.enum,
        jsonBody.put.properties.roles.items.enumNames,
      );

      jsonBody.put.properties.roles.items.enum = sortedRoleEnums;
      jsonBody.put.properties.roles.items.enumNames = sortedRoleEnumNames;
    }

    // Sanitize regex patterns in the schema to fix invalid escapes inside character classes
    if (jsonBody?.put) {
      jsonBody.put = sanitizeSchemaPatterns(jsonBody.put);
    }

    return jsonBody;
  }

  static async updateUser(data, brand) {
    const jsonBody = await request(`${this.basePath()}/${data.id_member}?brand=${brand}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static async getUserBusinessDetails(idMember, options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${this.basePath()}/${idMember}/business-details`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return jsonBody.result;
  }

  static async updateUserBusinessDetails(idMember, data, options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${this.basePath()}/${idMember}/business-details`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      ...(options ?? {}),
    });
    return jsonBody.result;
  }

  static async deleteUserBusinessDetails(idMember) {
    return request(`${this.basePath()}/${idMember}/business-details`, {
      method: 'DELETE',
    });
  }

  static async toggle(data) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/toggles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static async updatePartnershipsDetails(idMember, data) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/partnerships/${idMember}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static async getRegisterSchema(brand) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/register?brand=${brand}`, {
      method: 'OPTIONS',
    });

    // Sanitize regex patterns in the schema to fix invalid escapes inside character classes
    // that would fail with the Unicode flag used by AJV validator
    if (jsonBody?.post) {
      jsonBody.post = sanitizeSchemaPatterns(jsonBody.post);
    }

    return jsonBody;
  }

  static async registerUser(data) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static getRoles() {
    return request(`${window.configs.AUTH_HOST}/api/roles`);
  }

  static getUsersByRole(role, page = 1) {
    return request(`${window.configs.AUTH_HOST}/api/roles/users?role=${role}&page=${page}`);
  }

  static healthCheck() {
    return request(`${window.configs.AUTH_HOST}/api/auth/health`);
  }

  static async validateEmail(email) {
    return request(`${window.configs.AUTH_HOST}/api/validate-email?email=${email}`);
  }

  static async getEmailChangeHistory(userId) {
    return request(`${window.configs.AUTH_HOST}/api/auth/email-change/email-change-history/${userId}`);
  }

  static async getPhoneNumberChangeHistory(userId) {
    return request(`${window.configs.AUTH_HOST}/api/auth/phone-change/history/${userId}`);
  }

  static async getUserHistory(userId) {
    return request(`${window.configs.AUTH_HOST}/api/user-history/${userId}`);
  }

  static async getIdentityProviders(options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/identity-providers`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return jsonBody.result as Array<IdentityProvider>;
  }

  static async getAccountIdentities(accountId: string, options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${accountId}/identities`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return jsonBody.result as Array<AccountIdentity>;
  }

  static async createAccountIdentity(
    accountId: string,
    payload: { identity_provider_id: string; identifier_claim_value: string },
  ) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${accountId}/identities`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
    return jsonBody.result as AccountIdentity;
  }

  static async deleteAccountIdentity(accountId: string, identityId: string) {
    return request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${accountId}/identities/${identityId}`, {
      method: 'DELETE',
    });
  }

  static async sendOtpSmsCode(phone_prefix, phone, brand) {
    const payload = {
      brand,
      phone_number: {
        prefix: phone_prefix,
        phone,
      },
    };

    return request(`${window.configs.AUTH_HOST}/api/auth/otp/send-confirmation-code`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static async validateOtpSmsCode(phone_prefix, phone, code, brand) {
    const payload = {
      brand,
      code,
      phone_number: {
        prefix: phone_prefix,
        phone,
      },
    };

    return request(`${window.configs.AUTH_HOST}/api/auth/otp/validate-confirmation-code`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static async getSmsVerificationStats(params?: { signal?: AbortSignal; startDate?: string; endDate?: string }) {
    const { signal, startDate, endDate } = params || {};
    const qs = new URLSearchParams();
    if (startDate) {
      qs.set('startDate', startDate);
    }
    if (endDate) {
      qs.set('endDate', endDate);
    }
    const query = qs.toString();
    const url = `${window.configs.AUTH_HOST}/api/auth/sms-verification/stats${query ? `?${query}` : ''}`;
    return request(url, { method: 'GET', ...(signal ? { signal } : {}) });
  }

  static async getSmsVerificationWarning(userId: string) {
    return request(`${window.configs.AUTH_HOST}/api/auth/sms-verification/warning/${userId}`, {
      method: 'GET',
    });
  }

  static async setSmsVerificationWarning(userId: string, warningType: 'soft' | 'hard' | 'none') {
    return request(`${window.configs.AUTH_HOST}/api/auth/sms-verification/warning/${userId}`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ warningType }),
    });
  }
}
